import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import LoadingOverlay from '../components/LoadingOverlay';
import { useTerminologia } from '../hooks/useTerminologia';

const HOST_API = process.env.REACT_APP_URL;

const TIPOS_COEFICIENTE = [
    { label: 'Coeficiente M2',        value: 1 },
    { label: 'Coeficiente M2 - A',    value: 2 },
    { label: 'Coeficiente M2 - BCD',  value: 3 },
    { label: 'Coeficiente ARBA',      value: 4 },
    { label: 'Coeficiente Estatuto',  value: 5 },
    { label: 'Coeficiente Asamblea',  value: 6 },
];

const COEFICIENTE_EMPTY = {
    coeficienteUnidadFuncionalId: 0,
    unidadFuncionalId: null,
    coeficienteTipoId: null,
    valor:              null,
    fechaDesde:         null,
    fechaHasta:         null,
};

const fmtFecha = (v) => v ? new Date(v).toLocaleDateString('es-AR') : '—';
const fmtValor = (v) => v != null ? Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 6 }) : '—';
const tipoLabel = (id) => TIPOS_COEFICIENTE.find(t => t.value === id)?.label || id;

const Coeficientes = () => {
    const toast = useRef(null);
    const fileInputRef = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const t = useTerminologia();

    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');

    // ─── Data ────────────────────────────────────────────────────────────────────
    const [coeficientes, setCoeficientes] = useState([]);
    const [ufs,          setUfs]          = useState([]);
    const [loading,      setLoading]      = useState(false);

    // ─── Filtros ─────────────────────────────────────────────────────────────────
    const [filtroTipo, setFiltroTipo] = useState(null);

    // ─── Modal editar ────────────────────────────────────────────────────────────
    const [modalCoeficiente, setModalCoeficiente] = useState(false);
    const [form,             setForm]             = useState({ ...COEFICIENTE_EMPTY });
    const [submitting,       setSubmitting]        = useState(false);

    // ─── Modal importar Excel ─────────────────────────────────────────────────────
    const [modalExcel, setModalExcel] = useState(false);
    const [excelFile,  setExcelFile]  = useState(null);
    const [importing,  setImporting]  = useState(false);

    // ─── Carga UFs ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setUfs(data))
            .catch(() => {});
    }, [barrio?.id]);

    // ─── Carga coeficientes ──────────────────────────────────────────────────────
    const fetchCoeficientes = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            let url = `${HOST_API}api/coeficientes/GetCoeficientes?administracionId=${administracionId}&barrioId=${barrio.id}`;
            if (filtroTipo) url += `&coeficienteTipoId=${filtroTipo}`;
            const { data } = await axios.get(url);
            setCoeficientes(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los coeficientes', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, filtroTipo, administracionId]);

    useEffect(() => { fetchCoeficientes(); }, [fetchCoeficientes]);

    // ─── Editar coeficiente ──────────────────────────────────────────────────────
    const editarCoeficiente = (row) => {
        setForm({
            coeficienteUnidadFuncionalId: row.coeficienteUnidadFuncionalId,
            unidadFuncionalId:            row.unidadFuncionalId,
            coeficienteTipoId:            row.coeficienteTipoId,
            valor:                        row.valor,
            fechaDesde:                   row.fechaDesde ? new Date(row.fechaDesde) : null,
            fechaHasta:                   row.fechaHasta ? new Date(row.fechaHasta) : null,
        });
        setModalCoeficiente(true);
    };

    const guardarCoeficiente = async () => {
        if (!form.unidadFuncionalId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: `Seleccione la ${t.UnidadFuncional}`, life: 3000 }); return; }
        if (!form.coeficienteTipoId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el tipo de coeficiente', life: 3000 }); return; }
        if (form.valor == null)      { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el valor', life: 3000 }); return; }
        if (!form.fechaDesde)        { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese la fecha desde', life: 3000 }); return; }

        setSubmitting(true);
        try {
            const payload = {
                coeficienteUnidadFuncionalId: form.coeficienteUnidadFuncionalId,
                barrioId:                     barrio.id,
                administracionId,
                unidadFuncionalId:            form.unidadFuncionalId,
                coeficienteTipoId:            form.coeficienteTipoId,
                valor:                        form.valor,
                fechaDesde:                   form.fechaDesde,
                fechaHasta:                   form.fechaHasta,
            };
            await axios.post(`${HOST_API}api/coeficientes/SaveCoeficiente`, payload);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Coeficiente guardado correctamente', life: 3000 });
            setModalCoeficiente(false);
            fetchCoeficientes();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo guardar el coeficiente', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Eliminar coeficiente ────────────────────────────────────────────────────
    const eliminarCoeficiente = (row) => {
        confirmDialog({
            message:      `¿Eliminar el ${tipoLabel(row.coeficienteTipoId)} de ${t.UF} ${row.unidadFuncional}?`,
            header:       'Confirmar eliminación',
            icon:         'pi pi-exclamation-triangle',
            acceptLabel:  'Eliminar',
            rejectLabel:  'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/coeficientes/DeleteCoeficiente?coeficienteUnidadFuncionalId=${row.coeficienteUnidadFuncionalId}`);
                    toast.current.show({ severity: 'success', summary: 'Eliminado', detail: 'Coeficiente eliminado', life: 3000 });
                    fetchCoeficientes();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el coeficiente', life: 3000 });
                }
            },
        });
    };

    // ─── Importar Excel ───────────────────────────────────────────────────────────
    const handleExcelChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const ext = f.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(ext)) {
            toast.current.show({ severity: 'warn', summary: 'Formato inválido', detail: 'Solo se aceptan archivos .xlsx o .xls', life: 3000 });
            return;
        }
        setExcelFile(f);
    };

    const importarExcel = async () => {
        if (!excelFile) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione un archivo Excel', life: 3000 }); return; }

        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file',             excelFile);
            fd.append('administracionId', administracionId);
            fd.append('barrioId',         barrio.id);

            const { data } = await axios.post(`${HOST_API}api/coeficientes/ImportarExcel`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.current.show({
                severity: 'success',
                summary:  'Importación finalizada',
                detail:   `${data.importados} importados${data.noEncontrados ? `, ${data.noEncontrados} sin ${t.UF} encontrada` : ''}`,
                life:     4500,
            });
            setModalExcel(false);
            setExcelFile(null);
            fetchCoeficientes();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'Error al importar el archivo', life: 4000 });
        } finally {
            setImporting(false);
        }
    };

    const descargarPlantilla = () => {
        const csv = 'unidadFuncional;coeficienteM2;coeficienteM2A;coeficienteM2BCD;coeficienteArba;coeficienteEstatuto;coeficienteAsamblea;fechaDesde\n1;0.0123;0.0110;0.0105;0.0130;0.0120;0.0125;03/06/2026\n2;0.0098;0.0090;0.0085;0.0100;0.0095;0.0099;03/06/2026\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = 'plantilla_coeficientes.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    // ─── Templates de columna ─────────────────────────────────────────────────────
    const tipoTemplate       = (row) => tipoLabel(row.coeficienteTipoId);
    const valorTemplate      = (row) => fmtValor(row.valor);
    const fechaDesdeTemplate = (row) => fmtFecha(row.fechaDesde);
    const fechaHastaTemplate = (row) => fmtFecha(row.fechaHasta);
    const activoTemplate     = (row) => {
        const s = row.activo
            ? { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5', label: 'Activo' }
            : { bg: '#fef3f2', color: '#f04438', border: '#fecdca', label: 'No Activo' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {s.label}
            </span>
        );
    };
    const accionesTemplate   = (row) => (
        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
            <Button icon="pi pi-pencil" className="p-button-rounded p-button-text p-button-sm"
                tooltip="Editar" tooltipOptions={{ position: 'top' }}
                onClick={() => editarCoeficiente(row)} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                tooltip="Eliminar" tooltipOptions={{ position: 'top' }}
                onClick={() => eliminarCoeficiente(row)} />
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <h4>Coeficientes</h4>
            <br />
            <Toast ref={toast} />
            <ConfirmDialog />

<LoadingOverlay visible={loading} />

            {/* ─── Barra superior ──────────────────────────────────────────────── */}
            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="flex gap-2 flex-wrap align-items-end">
                    <div>
                        <label style={lbl}>Tipo de Coeficiente</label>
                        <Dropdown value={filtroTipo} options={TIPOS_COEFICIENTE} onChange={(e) => setFiltroTipo(e.value)}
                            placeholder="Todos" showClear className="p-inputtext-sm" style={{ width: 200 }} />
                    </div>
                    <Button label="Buscar" icon="pi pi-search" onClick={fetchCoeficientes} className="p-button-sm" />
                </div>
                <div className="flex gap-2">
                    <Button label="Nuevo Coeficiente" icon="pi pi-plus" className="p-button-sm"
                        onClick={() => { setForm({ ...COEFICIENTE_EMPTY }); setModalCoeficiente(true); }} />
                    <Button label="Importar Excel" icon="pi pi-file-excel" className="p-button-sm p-button-success"
                        onClick={() => { setModalExcel(true); setExcelFile(null); }} />
                </div>
            </div>

            {/* ─── Grilla ──────────────────────────────────────────────────────── */}
            <div className="grid">
                <div className="col-12">
                    <DataTable value={coeficientes} paginator rows={15} rowsPerPageOptions={[10, 15, 25, 50]}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay coeficientes registrados"
                        className="p-datatable-gridlines bg-white"
                        sortField="fechaDesde" sortOrder={-1}>
                        <Column field="unidadFuncional"  header={t.UF}                sortable style={{ width: '6rem', textAlign: 'center' }} />
                        <Column field="coeficienteTipo"  header="Tipo de Coeficiente" sortable body={tipoTemplate} style={{ width: '12rem' }} />
                        <Column field="valor"            header="Valor"               sortable body={valorTemplate} style={{ width: '8rem', textAlign: 'right' }} />
                        <Column field="fechaDesde"        header="Fecha Desde"         sortable body={fechaDesdeTemplate} style={{ width: '9rem' }} />
                        <Column field="fechaHasta"        header="Fecha Hasta"         sortable body={fechaHastaTemplate} style={{ width: '9rem' }} />
                        <Column field="activo"           header="Estado"              sortable body={activoTemplate} style={{ width: '7rem', textAlign: 'center' }} />
                        <Column header="Acciones" body={accionesTemplate} style={{ width: '7rem', textAlign: 'center' }} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Modal: Editar Coeficiente ─────────────────────────────────────── */}
            <Dialog header="Editar Coeficiente" visible={modalCoeficiente} style={{ width: '480px' }} modal
                onHide={() => setModalCoeficiente(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalCoeficiente(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardarCoeficiente} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>{t.UnidadFuncional} *</label>
                        <Dropdown value={form.unidadFuncionalId} options={ufs} optionLabel="unidadFuncional" optionValue="unidadFuncionalId"
                            onChange={(e) => setForm(f => ({ ...f, unidadFuncionalId: e.value }))}
                            placeholder={`Seleccionar ${t.UF}`} filter disabled={!!form.coeficienteUnidadFuncionalId} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Tipo de Coeficiente *</label>
                        <Dropdown value={form.coeficienteTipoId} options={TIPOS_COEFICIENTE}
                            onChange={(e) => setForm(f => ({ ...f, coeficienteTipoId: e.value }))}
                            placeholder="Seleccionar tipo" disabled={!!form.coeficienteUnidadFuncionalId} className="p-inputtext-sm" />
                    </div>
                    <div className="col-4">
                        <label style={lbl}>Valor *</label>
                        <InputNumber value={form.valor} onValueChange={(e) => setForm(f => ({ ...f, valor: e.value }))}
                            mode="decimal" minFractionDigits={2} maxFractionDigits={6} className="p-inputtext-sm" />
                    </div>
                    <div className="col-4">
                        <label style={lbl}>Fecha Desde *</label>
                        <Calendar value={form.fechaDesde} onChange={(e) => setForm(f => ({ ...f, fechaDesde: e.value }))}
                            dateFormat="dd/mm/yy" placeholder="dd/mm/aaaa" className="p-inputtext-sm" />
                    </div>
                    <div className="col-4">
                        <label style={lbl}>Fecha Hasta</label>
                        <Calendar value={form.fechaHasta} onChange={(e) => setForm(f => ({ ...f, fechaHasta: e.value }))}
                            dateFormat="dd/mm/yy" placeholder="dd/mm/aaaa" showButtonBar className="p-inputtext-sm" />
                    </div>
                </div>
            </Dialog>

            {/* ─── Modal: Importar Excel ────────────────────────────────────────── */}
            <Dialog header="Importar Coeficientes desde Excel" visible={modalExcel} style={{ width: '460px' }} modal
                onHide={() => setModalExcel(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalExcel(false)} disabled={importing} />
                        <Button label="Importar" icon="pi pi-upload" className="p-button-sm p-button-success" onClick={importarExcel} loading={importing} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    {/* Formato esperado */}
                    <div className="col-12">
                        <div style={{ background: '#f8f9fa', border: '1px solid #e4e7ec', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#344054' }}>
                            <strong>Formato del archivo:</strong><br />
                            <span style={{ color: '#667085' }}>Columnas (fila 1 = títulos, datos desde fila 2):</span>
                            <ol style={{ margin: '0.35rem 0 0 1rem', padding: 0, lineHeight: 1.8 }}>
                                <li><b>A:</b> {t.UnidadFuncional} (número)</li>
                                <li><b>B:</b> Coeficiente M2</li>
                                <li><b>C:</b> Coeficiente M2 - A</li>
                                <li><b>D:</b> Coeficiente M2 - BCD</li>
                                <li><b>E:</b> Coeficiente ARBA</li>
                                <li><b>F:</b> Coeficiente Estatuto</li>
                                <li><b>G:</b> Coeficiente Asamblea</li>
                                <li><b>H:</b> Fecha Desde</li>
                            </ol>
                            <span style={{ color: '#667085' }}>Las columnas de coeficiente son opcionales: se importa un registro por cada una que tenga valor.</span>
                        </div>
                        <Button label="Descargar plantilla" icon="pi pi-download" className="p-button-text p-button-sm mt-2"
                            onClick={descargarPlantilla} />
                    </div>

                    <div className="col-12">
                        <label style={lbl}>Archivo Excel (.xlsx / .xls) *</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                                onChange={handleExcelChange} />
                            <Button label={excelFile ? excelFile.name : 'Seleccionar archivo'}
                                icon="pi pi-file-excel"
                                className={`p-button-outlined p-button-sm ${excelFile ? 'p-button-success' : ''}`}
                                style={{ flex: 1, justifyContent: 'flex-start', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                onClick={() => fileInputRef.current.click()} />
                            {excelFile && (
                                <Button icon="pi pi-times" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                    onClick={() => { setExcelFile(null); fileInputRef.current.value = ''; }} />
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default Coeficientes;
